import { Link } from "react-router-dom";
import { ArrowLeft, Download, Mail, Instagram, Sparkles } from "lucide-react";
import { Logo } from "@/components/lunelle/Logo";
import { Countdown } from "@/components/lunelle/Countdown";
import { ShareKit } from "@/components/press/ShareKit";
import { ASSETS, BRAND_COLOURS, FACTS, INSTAGRAM } from "@/lib/press-data";
import { usePageMeta } from "@/lib/usePageMeta";

const Section = ({ title, children, id }) => (
  <section id={id} className="mt-20 border-t border-[#C86D51]/20 pt-10">
    <h2 className="font-serif text-4xl text-[#2C221E]">{title}</h2>
    <div className="mt-6">{children}</div>
  </section>
);

const CREATOR_MAIL = "mailto:Lunellesee@gmail.com?subject=Lunelle%20creator%20early%20access";

export default function PressPage() {
  usePageMeta("Press & Creators | Lunelle", "Lunelle press kit: story, fact sheet, logos, brand colours and a ready-made social share kit for creators ahead of the 4 November launch.");
  return (
    <div className="min-h-screen" data-testid="press-page">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-12">
        <Link to="/" data-testid="press-home-link"><Logo /></Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-[#5C4A42] hover:text-[#2C221E]"><ArrowLeft className="h-4 w-4" /> Back to Lunelle</Link>
      </header>
      <main className="mx-auto max-w-6xl px-5 pb-28 pt-10 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9E4429]">Press & creators</p>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-light leading-[1.02] text-[#2C221E] sm:text-7xl">Help us tell the story <em className="italic text-[#9E4429]">of every phase.</em></h1>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Countdown prefix="press" />
        </div>
        <a href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer" data-testid="press-instagram-link"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#C86D51]/30 px-5 py-2.5 text-sm font-semibold text-[#2C221E] transition-colors hover:border-[#9E4429] hover:text-[#9E4429]">
          <Instagram className="h-4 w-4" /> Follow {INSTAGRAM.handle} and tag us on launch day
        </a>

        <Section title="Our story">
          <div className="grid gap-8 md:grid-cols-[1.3fr_1fr]">
            <div className="space-y-4 text-lg leading-relaxed text-[#3D302B]">
              <p>Lunelle is a cycle and lifestyle app for women. Your cycle sits at the centre, and everything else flows from it: what to eat for your body type, a daily scripture or affirmation from your own faith, what to wear for your sign, and a budget that moves with your month.</p>
              <p className="rounded-2xl border border-dashed border-[#C86D51]/40 p-4 text-base text-[#6B5A52]" data-testid="press-founder-placeholder">[Founder story: who you are, why you started Lunelle, and the moment that sparked it]</p>
            </div>
            <dl className="rounded-[1.5rem] bg-[#2C221E] p-6 text-[#FBF5EE]" data-testid="press-fact-sheet">
              {FACTS.map(([k, v]) => (
                <div key={k} className="border-b border-white/10 py-3 last:border-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E8B99A]">{k}</dt>
                  <dd className="mt-1 text-sm">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Section>

        <Section title="Social share kit" id="share-kit"><ShareKit /></Section>

        <Section title="Logos & brand">
          <div className="grid gap-8 md:grid-cols-2">
            <ul className="space-y-2">
              {ASSETS.map(([label, file]) => (
                <li key={file}>
                  <a href={file} download data-testid={`press-asset-${file.split("/").pop().split(".")[0]}`}
                    className="flex items-center justify-between rounded-2xl border border-[#C86D51]/15 bg-white/70 px-5 py-4 text-sm text-[#2C221E] hover:border-[#9E4429]">
                    {label} <Download className="h-4 w-4 text-[#9E4429]" />
                  </a>
                </li>
              ))}
            </ul>
            <div>
              <div className="grid grid-cols-3 gap-3">
                {BRAND_COLOURS.map(([n, hex]) => (
                  <div key={hex}><div className="h-16 rounded-xl border border-black/5" style={{ background: hex }} /><p className="mt-1.5 text-xs text-[#5C4A42]">{n} · {hex}</p></div>
                ))}
              </div>
              <p className="mt-5 text-sm text-[#5C4A42]">Type: <span className="font-serif text-lg text-[#2C221E]">Cormorant Garamond</span> for headlines, Manrope for body text.</p>
            </div>
          </div>
        </Section>

        <Section title="Creators & press">
          <div className="rounded-[2rem] bg-[#F4E6DC] p-8 sm:p-10">
            <p className="max-w-2xl text-lg leading-relaxed text-[#3D302B]">Create content about cycles, wellness, faith or style? Apply to the Lunelle creator circle. Once you're approved, you get a personal invite code to share with your audience on launch day.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/creators" data-testid="press-creator-apply-link" className="inline-flex items-center gap-2 rounded-full bg-[#9E4429] px-5 py-3 text-sm font-semibold text-[#FBF5EE] hover:bg-[#843722]"><Sparkles className="h-4 w-4" /> Apply as a creator</Link>
              <a href={CREATOR_MAIL} data-testid="press-creator-email-button" className="inline-flex items-center gap-2 rounded-full border border-[#C86D51]/30 px-5 py-3 text-sm font-semibold text-[#2C221E] hover:border-[#9E4429]"><Mail className="h-4 w-4" /> Lunellesee@gmail.com</a>
              <a href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer" data-testid="press-creator-instagram-link" className="inline-flex items-center gap-2 rounded-full border border-[#C86D51]/30 px-5 py-3 text-sm font-semibold text-[#2C221E] hover:border-[#9E4429]"><Instagram className="h-4 w-4" /> {INSTAGRAM.handle}</a>
            </div>
          </div>
        </Section>
      </main>
    </div>
  );
}
